import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icon, Loader } from '@common/components';
import { faChevronRight, faAngleLeft, faAngleRight, faAnglesLeft, faAnglesRight, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { LABEL_KEYS } from '@dashboard/components/SyncMonitor/statuses';
import { useSyncRecentByStatusQuery } from '@dashboard/api/syncMonitor';
import DeliveryModeBadge from '@dashboard/components/SyncMonitor/DeliveryModeBadge';
import TagBadge, {
  CenterStatusBadge,
  HistoricSinceBadge,
} from '@dashboard/components/SyncMonitor/TagBadge/TagBadge';
import CopyId from '@dashboard/components/SyncMonitor/CopyId';
import { isSelectingText, onActivateKey } from '@dashboard/components/SyncMonitor/rowSelection';
import DayDetailBody from '@dashboard/components/SyncMonitor/DayDetailBody';
import classes from './RecentByStatusList.module.scss';

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function periodLabel(date, year, month, t) {
  if (date) return t('filterPeriodDay', { date });
  if (month) return `${MONTH_NAMES[month - 1]} ${year}`;
  if (year) return String(year);
  return t('filterPeriodAll');
}

export default function RecentByStatusList({
  status,
  centerIds,
  year,
  month,
  date,
  limit,
  tags,
  onClose,
}) {
  const { t } = useTranslation('dashboard\\SyncMonitorView', {
    useSuspense: false,
  });

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  // Debounce the search input by 300ms.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQ(searchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Reset page when filters change.
  useEffect(() => {
    setPage(1);
  }, [status, centerIds.join(','), tags.join(','), year, month, date, debouncedQ]);

  const { data: resp, isFetching } = useSyncRecentByStatusQuery(
    { status, centerIds, year, month, date, limit, page, tags, q: debouncedQ || undefined },
    { skip: !status },
  );

  const [expandedKey, setExpandedKey] = useState(null);
  const toggleExpand = useCallback((key) => {
    setExpandedKey((curr) => (curr === key ? null : key));
  }, []);

  if (!status) return null;

  const rows = resp?.data?.rows ?? [];
  const meta = resp?.data?.meta ?? { total: 0, page: 1, total_pages: 1, limit };
  const { total } = meta;
  const totalPages = meta.total_pages || 1;
  const currentPage = meta.page || 1;
  const fromRow = total === 0 ? 0 : (currentPage - 1) * limit + 1;
  const toRow = Math.min(currentPage * limit, total);
  const statusLabel = t(LABEL_KEYS[status] || status);
  const centersChipLabel = centerIds.length === 0
    ? t('filterAllCenters')
    : t('filterCentersCount', { count: centerIds.length });

  return (
    <div className={classes.panel}>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <div className={classes.titleRow}>
            <h3 className={classes.title}>
              {t('recentListTitleNeutral')}
              <span className={classes.count}>({total})</span>
            </h3>
            {totalPages > 1 && (
              <div className={classes.pager}>
                <button
                  type="button"
                  className={classes.pagerBtn}
                  onClick={() => setPage(1)}
                  disabled={currentPage <= 1}
                  aria-label="First page"
                >
                  <Icon icon={faAnglesLeft} />
                </button>
                <button
                  type="button"
                  className={classes.pagerBtn}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  aria-label="Previous page"
                >
                  <Icon icon={faAngleLeft} />
                </button>
                <span className={classes.pagerLabel}>
                  {t('pagerPageOf', { page: currentPage, total: totalPages })}
                </span>
                <button
                  type="button"
                  className={classes.pagerBtn}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  aria-label="Next page"
                >
                  <Icon icon={faAngleRight} />
                </button>
                <button
                  type="button"
                  className={classes.pagerBtn}
                  onClick={() => setPage(totalPages)}
                  disabled={currentPage >= totalPages}
                  aria-label="Last page"
                >
                  <Icon icon={faAnglesRight} />
                </button>
              </div>
            )}
          </div>

          <div className={classes.filtersRow}>
            <div className={classes.filters}>
              <span className={`${classes.chip} ${classes.chipStatus} ${classes[`chip_${status}`]}`}>
                <span className={classes.chipLabel}>{t('filterStatus')}</span>
                <strong>{statusLabel}</strong>
              </span>
              <span className={classes.chip}>
                <span className={classes.chipLabel}>{t('filterPeriod')}</span>
                <strong>{periodLabel(date, year, month, t)}</strong>
              </span>
              <span className={classes.chip}>
                <span className={classes.chipLabel}>{t('filterCenters')}</span>
                <strong>{centersChipLabel}</strong>
              </span>
              <span className={classes.chip}>
                <span className={classes.chipLabel}>{t('filterRange')}</span>
                <strong>{t('filterRangeValue', { from: fromRow, to: toRow, total })}</strong>
              </span>
              {debouncedQ && (
                <span className={classes.chip}>
                  <span className={classes.chipLabel}>{t('filterSearch')}</span>
                  <strong>{debouncedQ}</strong>
                </span>
              )}
            </div>

            <div className={classes.searchBox}>
              <Icon icon={faSearch} className={classes.searchIcon} />
              <input
                type="text"
                className={classes.searchInput}
                placeholder={t('searchInListPlaceholder')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              {searchInput && (
                <button
                  type="button"
                  className={classes.searchClear}
                  onClick={() => setSearchInput('')}
                  aria-label="Clear search"
                >
                  <Icon icon={faTimes} />
                </button>
              )}
            </div>
          </div>
        </div>
        <button
          type="button"
          className={classes.closeBtn}
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      {isFetching && (
        <div className={classes.loaderWrap}>
          <Loader />
        </div>
      )}
      {!isFetching && rows.length === 0 && (
        <p className={classes.empty}>{t('statusListEmpty')}</p>
      )}
      {!isFetching && rows.length > 0 && (
        <ul className={classes.list}>
          {rows.map((r) => {
            const key = `${r.center_uuid}|${r.date}`;
            const isExpanded = expandedKey === key;
            return (
              <li key={key} className={isExpanded ? classes.itemExpanded : ''}>
                <div
                  role="button"
                  tabIndex={0}
                  className={classes.row}
                  onClick={() => !isSelectingText() && toggleExpand(key)}
                  onKeyDown={onActivateKey(() => toggleExpand(key))}
                  aria-expanded={isExpanded}
                >
                  <Icon
                    icon={faChevronRight}
                    className={`${classes.chevron} ${isExpanded ? classes.chevronOpen : ''}`}
                  />
                  <span className={classes.date}>{r.date}</span>
                  <span className={classes.centerName}>
                    {r.center_name}
                    {r.center_external_id ? (
                      <span className={classes.centerExt}> · <CopyId value={r.center_external_id} /></span>
                    ) : null}
                  </span>
                  <CenterStatusBadge status={r.center_status} />
                  {(r.tags || []).map((tag) => (
                    <TagBadge key={tag} tag={tag} />
                  ))}
                  <HistoricSinceBadge date={r.historic_since} />
                  {r.delivery_mode ? (
                    <DeliveryModeBadge mode={r.delivery_mode} size="xs" />
                  ) : null}
                </div>
                {isExpanded && (
                  <div className={classes.accordion}>
                    <DayDetailBody centerId={r.center_uuid} date={r.date} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

    </div>
  );
}

RecentByStatusList.propTypes = {
  status: PropTypes.string,
  centerIds: PropTypes.arrayOf(PropTypes.string),
  year: PropTypes.number,
  month: PropTypes.number,
  date: PropTypes.string,
  limit: PropTypes.number,
  tags: PropTypes.arrayOf(PropTypes.string),
  onClose: PropTypes.func.isRequired,
};

RecentByStatusList.defaultProps = {
  status: null,
  centerIds: [],
  year: null,
  month: null,
  date: null,
  limit: 50,
  tags: [],
};
